import React, { useMemo } from "react";
import styled from "styled-components";
import {
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableActionButton,
    DataTableCell,
    DataTableContainer,
    DataTableEditActionButton,
    DataTableHeaderCell,
    FlexContainer,
    Text,
    PortalExportLink,
    PortalIconActionButton,
    PortalIconActionButtonIcon,
    PortalSearchInput,
    PortalTableActions,
    PortalTableControls,
    PortalTableToolbar,
    Toast
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import LicenseIcon from "src/img/new/license-white.svg";
import UpArrow from "src/img/employeeList/SortUpArrow.svg";
import DownArrow from "src/img/employeeList/SortDownArrow.svg";
import { useState, useEffect } from "react";
import { capitalizeString, customNames } from "src/utils/helpers";
import { Forms } from "src/scenes/Setting/Setting";
import pluralize from "pluralize";
import { API_URL } from "src/scenes/App";
import { UserContext } from "src/scenes/App/UserContext";
import axios from "axios";
import { UserKeys } from "src/constants/userDetails";
import DeleteUsersDialog from "./DeleteUsersDialog";
import ChatPermissionCard from "./ChatPermissionTableCard";
import ClientRoleAndPermissionsForm from "./ClientRoleAndPermissionForm";
import ClientDocumentsModal from "./ClientDocumentsModal";
import * as PERMISSIONS from "src/constants/permissions";
import { ManageLicenseModal } from "./ManageLicenseModals";

export const EnumLicenseStatus = {
    NOT_STARTED: "notStarted",
    ACTIVE: "active",
    EXPIRED: "expired",
    TERMINATED: "terminated",
    RENEWED: "renewed",
    INVALID_STATUS: "invalidStatus",
    INITIAL_PAYMENT_REQUIRED: "initialPaymentRequired",
};

export const EnumAppLicenseStatus = {
    NOT_STARTED: {
        COLOR: "#171A22", BACKGROUND: "rgba(91,110,245,0.12)", TEXT: "Not Started"
    },
    ACTIVE: {
        COLOR: "#26A26B", BACKGROUND: "#E6FFF8", TEXT: "Active License"
    },
    SEND_RENEWAL_LINK: {
        COLOR: "#EFE7FF", BACKGROUND: "#633FE4", TEXT: "Payment Required"
    },
    TERMINATED: {
        COLOR: "#FF3B3B", BACKGROUND: "#FDE3E3", TEXT: "Renewal Not Available"
    },
    INITIAL_PAYMENT_REQUIRED: {
        COLOR: "#EFE7FF", BACKGROUND: "#633FE4", TEXT: "Payment Required"
    },
    DEFAULT: {
        COLOR: "#FFF", BACKGROUND: "#000", TEXT: "Invalid Status"
    }
};

const EnumUserStatus = {
    AWAITING_INVITE: { COLOR: "var(--calendar-text, #171A22)", TEXT: "Awaiting Invite" },
    PENDING: { COLOR: "var(--calendar-text, #171A22)", TEXT: "Pending" },
    ACTIVE: { COLOR: "#36BE24", TEXT: "Active" },
    EXPIRED: { COLOR: "#FF3B3B", TEXT: "Expired" },
    DEFAULT: { COLOR: "var(--calendar-text, #171A22)", TEXT: "Invalid Status" }
};

const UpperButtons = styled(Button)`
    border-radius: 8px !important;
    border: ${p => p.border || "none"};
    height: 42px;
    max-width: 149px;
    width: 100%;
    color: ${p => p.color || "#FFF"};
    background-color: ${p => p.backgroundColor || "#171A22"};
    box-sizing: border-box;
    font-size: 0.875rem;
    font-weight: 700;
    padding: ${p => p.padding};
    display: flex;
    align-items: center;
    margin-bottom: 0;
`;

const ButtonLabel = styled(DataTableActionButton)`
    height: 2rem;
    background: ${p => p.background};
    color: ${p => p.color};
    border: ${p => p.border || "none"};
    font-size: 0.75rem;
    font-weight: ${p => p.weight || 500};
    text-align: center;
    width: ${p => p.width};
    box-sizing: border-box;
    border-radius: 7px;
    cursor: ${p => p.cursor || "auto"};
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    img {
        margin-left: auto;
        padding-left: 5px;
    }
`;

const SortableColumns = {
    FIRST_NAME: 1,
    LAST_NAME: 2,
    EMAIL: 3,
    PHONE_NUMBER: 4,
    LICENSE_TYPE: 9,
    APP_LICENSE: 11,
    USER_INVITES: 13,
    USER_STATUS: 15,
};

const Count = styled.span`
    text-align: left;
    font: normal normal medium 14px/16px Manrope;
    letter-spacing: 0px;
    color: var(--calendar-text, #171A22);
    opacity: 1;
`;

const ClientLicenseTable = (props) => {
    const userContext = React.useContext(UserContext);
    const userPermissions = userContext.permissions || [];

    const { showPurchaseLicense, addClient, sendRenewalLink, sendInvite, updateClient, isFreeTrialLicense, clientLicenseArray, refreshTableOnClientSetPermission, licenseTiers } = props;
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [clients, setClients] = useState([]);
    const [selectAllUsers, setSelectAllUsers] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [appLicenseSortOrder, setAppLicenseSortOrder] = useState(EnumLicenseStatus.NOT_STARTED);
    const [inviteSort, setInviteSort] = useState("active");
    const token = localStorage.getItem(UserKeys.TOKEN);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUserPermission, setShowUserPermission] = useState(false);
    const [selectedClient, setSelectedClient] = useState();
    const [showFilesModal, setShowFilesModal] = useState(false);
    const [selectedClientFiles, setSelectedClientFiles] = useState(null);
    const [showManageLicenseModal, setShowManageLicenseModal] = useState(false);
    const [selectedLicenseClient, setSelectedLicenseClient] = useState(null);

    const uuidList = useMemo(() => {
        return selectedUsers.map(e => e.employeeUuid);
    }, [selectedUsers]);

    useEffect(() => {
        setClients(clientLicenseArray);
    }, [clientLicenseArray]);

    const canViewDocuments = () =>
        PERMISSIONS.hasImplicitPermission(
            PERMISSIONS.DOCUMENTS_VIEW,
            userPermissions
        );
    
    const handleViewFiles = (client) => {
        setSelectedClientFiles(client);
        setShowFilesModal(true);
    };

    const openManageLicenseModal = (client) => {
        setSelectedLicenseClient(client);
        setShowManageLicenseModal(true);
    };

    const closeLicenseModals = () => {
        setShowManageLicenseModal(false);
        setSelectedLicenseClient(null);
    };

    const updateClientLicenseTier = (updatedClient) => {
        setClients(prevClients => prevClients.map(client =>
            client.employeeUuid === updatedClient.employeeUuid ? updatedClient : client
        ));
    };

    /** FUNCTIONS */
    const handleSearchUser = (e) => {
        const value = e.target.value;
        if (value.length) {
            const filteredClients = clients.filter(client =>
                client.firstName.toLowerCase().includes(value.toLowerCase()) || client.lastName.toLowerCase().includes(value.toLowerCase())
            );

            setClients(filteredClients);
        }
        else {
            setClients(clientLicenseArray);
        }
    };

    const handleSortColumn = (columnIndex) => {
        if (!clients.length) return;
        if (!isASortableColumn(columnIndex)) return;

        const nextSortDirection = sortColumn === columnIndex && sortDirection === "asc" ? "desc" : "asc";
        setSortColumn(columnIndex);
        setSortDirection(nextSortDirection);

        if ([SortableColumns.APP_LICENSE, SortableColumns.USER_STATUS].includes(columnIndex)) {
            return sortByLicenseStatus();
        }
        else if (columnIndex === SortableColumns.USER_INVITES) {
            return sortByInvite();
        }
        else {
            let fieldName;
            switch (columnIndex) {
                case SortableColumns.FIRST_NAME:
                    fieldName = "firstName";
                    break;
                case SortableColumns.LAST_NAME:
                    fieldName = "lastName";
                    break;
                case SortableColumns.EMAIL:
                    fieldName = "email";
                    break;
                case SortableColumns.PHONE_NUMBER:
                    fieldName = "phoneNumber";
                    break;
                case SortableColumns.LICENSE_TYPE:
                    fieldName = "licenseType";
                    break;
                default:
                    fieldName = null;
                    break;
            }

            if (fieldName === null) return;

            return sortFields(fieldName, nextSortDirection);
        }
    };

    const sortByInvite = () => {
        let next;
        const Status = {
            ACTIVE: "active",
            INVITE_SENT: "inviteSent",
            INVITE_NOT_SENT: "inviteNotSent",
        };

        switch (inviteSort) {
            case Status.ACTIVE:
                next = Status.INVITE_SENT;
                break;
            case Status.INVITE_SENT:
                next = Status.INVITE_NOT_SENT;
                break;
            default:
                next = Status.ACTIVE;
                break;
        }

        const sortedClients = [...clients].sort((a, b) => {
            switch (inviteSort) {
                case Status.ACTIVE:
                    return a.licenseStatus === EnumLicenseStatus.ACTIVE ? -1 : 1;
                case Status.INVITE_SENT:
                    return a.hasSentInvite ? -1 : 1;
                case Status.INVITE_NOT_SENT:
                    if (!a.hasSentInvite && a.licenseStatus !== EnumLicenseStatus.ACTIVE) {
                        return -1;
                    }
                    return 1;
                default:
                    return 0;
            }
        });

        setClients(sortedClients);
        setInviteSort(next);
    };

    const sortByLicenseStatus = () => {
        const { ACTIVE, EXPIRED, NOT_STARTED } = EnumLicenseStatus;
        let next;

        const sortedClients = [...clients].sort((a, b) => {
            if (a.licenseStatus === appLicenseSortOrder) return -1;
            if (b.licenseStatus === appLicenseSortOrder) return 1;
            return 0;
        });

        switch (appLicenseSortOrder) {
            case NOT_STARTED:
                next = ACTIVE;
                break;
            case ACTIVE:
                next = EXPIRED;
                break;
            default:
                next = NOT_STARTED;
                break;
        }
        setClients(sortedClients);
        setAppLicenseSortOrder(next);
    };

    const sortFields = (fieldName, direction = sortDirection) => {
        const sortedClients = [...clients].sort((a, b) => {
            const valueA = a[fieldName] ?? "";
            const valueB = b[fieldName] ?? "";
            const comparison = valueA.localeCompare(valueB);
            return direction === "asc" ? comparison : -comparison;
        });

        return setClients(sortedClients);
    };

    const headings = [
        <Checkbox readOnly checked={selectAllUsers} onChange={() => ""} />,
        "First Name", "Last Name", "Email", "Phone Number", "Company Name", "Permissions", "Uploaded Files",
        "License Name", "License Duration", "End Date", "App License",
        "View/Edit User", "User Invites", "Access Code/ID", "User Status"
    ];

    const handleMainCheckbox = (e) => {
        const isChecked = !e.target.checked;
        if (!isChecked) {
            setSelectedUsers([]);
        }
        else {
            setSelectedUsers(clients);
        }
        setSelectAllUsers(!selectAllUsers);
    };

    const addOrRemoveClientFromSelected = (event, client) => {
        const isChecked = event.target.checked;
        return isChecked ? setSelectedUsers([...selectedUsers, client])
            : setSelectedUsers(selectedUsers.filter(obj => obj["uuid"] !== client.uuid));
    };

    const checkIfClientIsSelected = (uuid) => selectedUsers.some(obj => obj["uuid"] === uuid);
    const isASortableColumn = (index) => {
        for (const key in SortableColumns) {
            if (SortableColumns[key] === index) {
                return true;
            }
        }
        return false;
    };

    const deleteClientUsers = async () => {
        try {
            const response = await axios.post(`${API_URL}/clients/deleteUsers/`, { "uuids": uuidList }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (response) {
                setShowDeleteModal(false);
                window.location.reload();
            }
        } catch (error) {
            console.log(error);
            Toast.error("Unable to delete clients. Something went wrong");
        }
    };

    /** RENDERS */
    /**
     * 
     * @param {string} licenseStatus
     * @param {bool} hasSentInvite
     * @param {bool|string} licenseType
     * @returns <Text />
     */
    const renderUserStatus = (licenseStatus, hasSentInvite, licenseType = false) => {
        let userStatus = EnumUserStatus.DEFAULT;

        if ([EnumLicenseStatus.EXPIRED, EnumLicenseStatus.TERMINATED].includes(licenseStatus)) {
            userStatus = EnumUserStatus.EXPIRED;
        }
        else if (licenseStatus === EnumLicenseStatus.ACTIVE) {
            userStatus = EnumUserStatus.ACTIVE;
        }

        else if (hasSentInvite) {
            userStatus = EnumUserStatus.PENDING;
        }
        else if (licenseStatus === EnumLicenseStatus.NOT_STARTED && !hasSentInvite) {
            userStatus = EnumUserStatus.AWAITING_INVITE;
        } else if (licenseStatus === EnumLicenseStatus.INITIAL_PAYMENT_REQUIRED) {
            return <Text align="left" color={"#FF3B3B"} weight="500">{"12 Months"}</Text>;
        }

        if (!licenseType) {
            // display User Status
            return <Text align="left" color={userStatus.COLOR} weight="500">
                {userStatus.TEXT}
            </Text>;
        }

        // display License Type
        if (userStatus.TEXT === EnumUserStatus.AWAITING_INVITE.TEXT) { //Awaiting & Pending uses the same `License Type` color
            return <Text align="left" color={EnumUserStatus.PENDING.COLOR} weight="500">
                {licenseType}
            </Text>;
        }

        return <Text align="left" color={userStatus.COLOR} weight="500">
            {licenseType}
        </Text>;
    };

    /**
     * 
     * @param {object} client 
     * @returns <ButtonLabel />
     */
    const renderAppLicense = (client) => {
        let status;
        let isExpired = false;

        switch (client.licenseStatus) {
            case EnumLicenseStatus.NOT_STARTED:
                status = EnumAppLicenseStatus.NOT_STARTED;
                break;
            case EnumLicenseStatus.ACTIVE:
                status = EnumAppLicenseStatus.ACTIVE;
                break;
            case EnumLicenseStatus.EXPIRED:
                status = EnumAppLicenseStatus.SEND_RENEWAL_LINK;
                isExpired = true;
                break;
            case EnumLicenseStatus.TERMINATED:
                status = EnumAppLicenseStatus.TERMINATED;
                break;
            case EnumLicenseStatus.INITIAL_PAYMENT_REQUIRED:
                status = EnumAppLicenseStatus.INITIAL_PAYMENT_REQUIRED;
                break;
            default:
                status = EnumAppLicenseStatus.DEFAULT;
                status.TEXT = client.licenseStatus;
                break;
        }

        return <ButtonLabel background={status.BACKGROUND}
            color={status.COLOR} width="8.625rem"
            cursor={isExpired ? "pointer" : "auto"}
            onClick={() => isExpired ? sendRenewalLink(client) : ""}
        >
            {status.TEXT}
        </ButtonLabel>;
    };

    const renderInviteButton = (client) => {
        if (client.companyQrCodeUuid) {
            return <ButtonLabel style={{ backgroundColor: "white", color: "#36BE24", borderStyle: "solid", borderWidth: 1, borderColor: "#36BE24", whiteSpace: "normal" }}
                width="5.875rem" disabled
            >Registered via QR code</ButtonLabel>;
        }
        if ([EnumLicenseStatus.ACTIVE, EnumLicenseStatus.EXPIRED].includes(client.licenseStatus)) {
            return "-";
        }
        else {
            if (!client.hasSentInvite) {
                return <ButtonLabel cursor="pointer" background="var(--calendar-action-primary-bg, #5B6EF5)" color="#FFF"
                    width="5.875rem" onClick={() => sendInvite([client])}
                >Send Invite</ButtonLabel>;
            }
            else {
                return <ButtonLabel cursor="pointer" background="#FFF" color="#171A22"
                    border="1px solid #D8DEEC" width="5.875rem"
                    onClick={() => sendInvite([client])}
                >Resend Invite</ButtonLabel>;
            }
        }
    };

    const SortingIcon = ({ sortingStatus }) => {
        const imgStyle = {};

        if (sortingStatus === "asc") {
            return <img src={UpArrow} alt="Ascending" style={imgStyle} />;
        }

        if (sortingStatus === "desc") {
            return <img src={DownArrow} alt="Descending" style={imgStyle} />;
        }

        return <></>;
    };

    const renderHeadings = headings.map((heading, index) => {
        const sortable = isASortableColumn(index);
        return (
            <DataTableHeaderCell key={index}
                onClick={(e) => index > 0 ? handleSortColumn(index) : handleMainCheckbox(e)}
                style={{
                    textAlign: index > 0 ? "left" : "center",
                    padding: "1.0625rem 1.4375rem",
                    cursor: sortable ? "pointer" : "auto",
                }}
            >
                <HeaderContainer>
                    <span>{heading}</span>
                    {sortable && <SortingIcon sortingStatus={sortColumn === index ? sortDirection : ""} />}
                </HeaderContainer>
            </DataTableHeaderCell>
        );
    });

    const renderRows = clients.map((client, index) => {
        const permissionIds = client?.roleAndPermissions?.permissionIds ? JSON.parse(client?.roleAndPermissions?.permissionIds) : [];
        const displayPermissionIds = permissionIds.filter(permissionId => permissionId !== PERMISSIONS.WEB_DASHBOARD_ACCESS);

        return (
            <tr key={index}
                className={checkIfClientIsSelected(client.uuid) ? "selected" : ""}
            >
                <DataTableCell paddingLeft="0" textAlign="center">
                    <label>
                        <Checkbox readOnly
                            checked={checkIfClientIsSelected(client.uuid) || selectAllUsers}
                            value={client.uuid}
                            onChange={(e) => addOrRemoveClientFromSelected(e, client)}
                        />
                    </label>
                </DataTableCell>
                <DataTableCell className="change-color-on-select">{capitalizeString(client.firstName)}</DataTableCell>
                <DataTableCell className="change-color-on-select">{capitalizeString(client.lastName)}</DataTableCell>
                <DataTableCell className="change-color-on-select">{client.email}</DataTableCell>
                <DataTableCell className="change-color-on-select">{client["phoneNumber"] || ""}</DataTableCell>
                <DataTableCell className="change-color-on-select">{client?.customCompanyName || ""}</DataTableCell>
                <DataTableCell className="change-color-on-select" textAlign="center">
                    <div
                        style={{ cursor: "pointer", justifyContent: "center", display: "flex" }}
                        onClick={() => {
                            setSelectedClient(client);
                            setShowUserPermission(true);
                        }}
                    >
                        <ChatPermissionCard permissionIds={displayPermissionIds} />
                    </div>
                </DataTableCell>
                <DataTableCell className="change-color-on-select">
                    {(() => {
                        const requiredDocs = client?.licenseTier?.requiredDocuments || [];
                        if (requiredDocs.length === 0) {
                            return (
                                <span>-</span>
                            );
                        }

                        const uploadedCount = requiredDocs.filter(doc => doc?.upload?.url).length;
                        const approvedCount = requiredDocs.filter(
                            doc => doc?.upload?.approvedAt && doc?.upload?.approvedBy
                        ).length;
                        const totalCount = requiredDocs.length;
                        const isMismatch = approvedCount < uploadedCount;
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "6px"
                                }}
                            >
                                <DataTableEditActionButton
                                    onClick={() => {
                                        if (!canViewDocuments()) {
                                            Toast.error("You don't have permission to view documents.");
                                            return;
                                        }
                                        handleViewFiles(client);
                                    }}
                                > View Files
                                </DataTableEditActionButton>
                                <Count style={{ color: isMismatch ? "#FF3B3B" : "var(--calendar-text, #171A22)" }}>
                                    {approvedCount} of {totalCount} Confirmed
                                </Count>
                            </div>
                        );
                    })()}
                </DataTableCell>
                <DataTableCell className="change-color-on-select">
                    <ButtonLabel
                        width="123px"
                        height="32px"
                        background="#633FE4"
                        color="#FFF"
                        cursor="pointer"
                        onClick={() => openManageLicenseModal(client)}
                    >
                        {client?.licenseTier?.tierName ?? "-"}
                    </ButtonLabel>
                </DataTableCell>
                <DataTableCell textAlign="center">{renderUserStatus(client.licenseStatus, client.hasSentInvite, client.licenseType)}</DataTableCell>
                <DataTableCell style={{ minWidth: "7.5rem" }}>
                    <Text align={client.endDate ? "left" : "center"}
                        color={checkIfClientIsSelected(client.uuid) ? "#171A22" : "var(--calendar-text, #171A22)"}
                        style={{ whiteSpace: "normal" }}
                    >
                        {client.endDate || "-"}
                    </Text>

                </DataTableCell>
                <DataTableCell textAlign="center">{renderAppLicense(client)}</DataTableCell>
                <DataTableCell textAlign="center">
                    <DataTableEditActionButton
                        onClick={() => updateClient(client)}
                    >
                        View/Edit
                    </DataTableEditActionButton>
                </DataTableCell>
                <DataTableCell textAlign="center">{renderInviteButton(client)}</DataTableCell>
                <DataTableCell textAlign="center"><ButtonLabel background="#000" color="#FFF" width="6.25rem">{client.accessToken}</ButtonLabel></DataTableCell>
                <DataTableCell textAlign="center">
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        {renderUserStatus(client.licenseStatus, client.hasSentInvite)}
                    </div>
                </DataTableCell>
            </tr>
        );
    });

    const showEmptyRows = () => {
        const emptyRows = 10 - clients.length;

        if (emptyRows < 1)
            return;

        return Array.from({ length: emptyRows }, (_, i) => (
            <tr key={"empty_" + i} className="placeholder-row">
                <DataTableCell $empty paddingLeft="0" textAlign="center">&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
            </tr>
        ));
    };

    return (
        <>
            <LicenseTable
                handleSearchUser={handleSearchUser}
                showPurchaseLicense={showPurchaseLicense}
                addClient={addClient}
                renderHeadings={renderHeadings}
                renderRows={renderRows}
                showEmptyRows={showEmptyRows}
                isFreeTrialLicense={isFreeTrialLicense}
                filter={props.filter}
                selectedUsers={selectedUsers}
                selectAllUsers={selectAllUsers}
                setShowDeleteModal={setShowDeleteModal}
                sendInvite={sendInvite}
            />
            {showDeleteModal && (
                <DeleteUsersDialog closeDeleteDialog={() => setShowDeleteModal(false)} onConfirm={deleteClientUsers} selectedUsers={selectedUsers} />
            )}
            {showUserPermission && (
                <ClientRoleAndPermissionsForm
                    user={props.user}
                    showDialog={showUserPermission}
                    handleClose={() => setShowUserPermission(false)}
                    selectedClient={selectedClient}
                    token={token}
                    onFormSubmissionSuccessful={() => {
                        refreshTableOnClientSetPermission();
                        setShowUserPermission(false);
                    }}
                />
            )}

            {showFilesModal && selectedClientFiles && (
                <ClientDocumentsModal
                    open={showFilesModal}
                    onClose={() => setShowFilesModal(false)}
                    headerTitle="User Documents"
                    requiredDocuments={selectedClientFiles?.licenseTier?.requiredDocuments || []}
                    refresh={refreshTableOnClientSetPermission}
                    userDetails={{
                        employeeUuid: selectedClientFiles.employeeUuid,
                        firstName: selectedClientFiles.firstName,
                        lastName: selectedClientFiles.lastName,
                        customCompanyName: selectedClientFiles.customCompanyName,
                        email: selectedClientFiles.email,
                        phoneNumber: selectedClientFiles.phoneNumber,
                        referralCode: selectedClientFiles.referralCode
                    }}
                />
            )}

            <ManageLicenseModal
                show={showManageLicenseModal}
                client={selectedLicenseClient}
                licenseTiers={licenseTiers}
                token={token}
                onClose={closeLicenseModals}
                onLicenseTierChanged={updateClientLicenseTier}
                refreshTable={refreshTableOnClientSetPermission}
            />
        </>

    );
};

function LicenseTable(props) {
    const handleClick = () => {
        window.location.href = Forms.RENEWALS_AND_EARNINGS;
    };

    const userContext = React.useContext(UserContext);
    const {
        handleSearchUser,
        showPurchaseLicense,
        addClient, renderHeadings,
        renderRows, showEmptyRows,
        isFreeTrialLicense,
        filter,
        selectedUsers,
        selectAllUsers,
        setShowDeleteModal,
        sendInvite
    } = props;

    return (
        <FlexContainer>
            <PortalTableToolbar maxWidth="1908px">
                <PortalTableControls>
                    <PortalSearchInput placeholder={`Search ${pluralize.singular(customNames.clients).toLowerCase()} user`} type="text" onChange={(e) => handleSearchUser(e)} />
                    <div style={{ pointerEvents: renderRows.length === 0 ? "none" : "auto", opacity: renderRows.length === 0 ? 0.7 : 1 }}>
                        <PortalExportLink
                            url={`${API_URL}/company/clientLicense/export/${filter}`}
                            filename={`${pluralize.singular(customNames.clients)}List.csv`}
                            user={userContext}
                            width="11.75rem"
                        > Export List CSV
                        </PortalExportLink>
                    </div>
                    <Button
                        backgroundColor="#E9CEFF"
                        onClick={handleClick}
                        color="#8551AE"
                        borderRadius="10px !important"
                        size="0.875rem"
                        weight="700"
                        width="202px"
                        height="39px"
                        border="1px solid #8551AE"
                        style={{ maxWidth: "15.125rem", boxShadow: "0px 3px 6px #00000029" }}
                    >
                        View Renewals & Earnings
                    </Button>
                    {
                        !isFreeTrialLicense && (
                            <PortalIconActionButton
                                height="42px"
                                borderRadius="8px"
                                padding="0 1rem"
                                width="auto"
                                onClick={() => showPurchaseLicense(true)}
                            >
                                <PortalIconActionButtonIcon src={LicenseIcon} />
                                Purchase Trial Licenses
                            </PortalIconActionButton>
                        )
                    }
                    {
                        (selectedUsers.length > 0 || selectAllUsers > 0) && <Button
                            backgroundColor="#FFF5F5"
                            onClick={() => setShowDeleteModal(true)}
                            color="#FF3B3B"
                            borderRadius="10px !important"
                            size="0.875rem"
                            weight="700"
                            width="110px"
                            height="39px"
                            border="1px solid #FF3B3B"
                            style={{ maxWidth: "15.125rem", boxShadow: "0px 3px 6px #00000029" }}
                        >
                            Delete User
                        </Button>
                    }
                    {(selectedUsers.length > 0 || selectAllUsers.length > 0) && <UpperButtons
                        padding="0 4.5rem !important"
                        backgroundColor="var(--calendar-action-primary-bg, #5B6EF5)"
                        onClick={() => sendInvite(selectedUsers)}
                    >
                        Send Invite
                    </UpperButtons>}
                </PortalTableControls>
                <PortalTableActions>
                    <ActivateFeatureButton
                        width="149px"
                        onClick={() => addClient()}
                    >
                        Add {capitalizeString(pluralize.singular(customNames.clients))}
                    </ActivateFeatureButton>
                </PortalTableActions>
            </PortalTableToolbar>

            <DataTableContainer maxWidth="1908px">
                <DataTable minWidth="1520px">
                    <thead>
                        <tr>{renderHeadings}</tr>
                    </thead>
                    <tbody>
                        <>
                            {renderRows}
                            {showEmptyRows()}
                        </>
                    </tbody>
                </DataTable>
            </DataTableContainer>
        </FlexContainer>
    );
}

export default ClientLicenseTable;
